import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import axios from '../config/api';
import { API_URL } from '../config/api';
import { useCache } from '../config/CacheContext';
import PollCard from '../components/PollCard';
import CustomPollCard from '../components/CustomPollCard';
import TrendingTicker from '../components/TrendingTicker';
import { MdKeyboardArrowDown, MdKeyboardArrowUp, MdInfo, MdSearch, MdClear } from 'react-icons/md';

function PollsPage() {
  const { tags, loadTags } = useCache();
  const [polls, setPolls] = useState([]);
  const [customPolls, setCustomPolls] = useState([]);
  const [top10Tags, setTop10Tags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [isTagMenuCollapsed, setIsTagMenuCollapsed] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [unansweredFirst, setUnansweredFirst] = useState(false);
  const [sortByInterests, setSortByInterests] = useState(false);
  const [statusFilter, setStatusFilter] = useState(null);
  const [stats, setStats] = useState({ totalPolls: 0, answeredPolls: 0, unansweredPolls: 0 });
  const [overallStats, setOverallStats] = useState({ totalPolls: 0, answeredPolls: 0 });
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const location = useLocation();
  const [favoritedPolls, setFavoritedPolls] = useState({});

  // Track if this is the initial mount
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tagParam = params.get('tag');
    const unansweredParam = params.get('unanswered_first');
    const filterParam = params.get('filter');
    
    // Load tags from cache (fast) and stats
    loadTags();
    loadStats();
    
    if (tagParam) {
      setSelectedTags([tagParam]);
      setHasUserInteracted(true);
      setStatusFilter('unanswered');
    } else if (filterParam) {
      setSelectedTags([]);
      setHasUserInteracted(true);
      if (filterParam === 'answered') {
        setStatusFilter('answered');
      } else if (filterParam === 'unanswered') {
        setStatusFilter('unanswered');
      } else {
        setStatusFilter('all');
      }
    } else {
      // No filters - show all polls by default
      setSelectedTags([]);
      setSearchKeyword('');
      setSearchInput('');
      setHasUserInteracted(true);
      setStatusFilter('all');
    }
    
    if (unansweredParam === 'true') {
      setUnansweredFirst(true);
    } else {
      setUnansweredFirst(false);
    }
    
    setIsInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, location.pathname]);

  useEffect(() => {
    // Only load polls after initialization and when we have a valid state
    if (isInitialized && hasUserInteracted && statusFilter !== null) {
      loadPolls(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized, selectedTags, statusFilter, hasUserInteracted, searchKeyword, sortByInterests]);

  useEffect(() => {
    const handleScroll = () => {
      if (loading || loadingMore || !hasMore) return;
      
      const scrollHeight = document.documentElement.scrollHeight;
      const scrollTop = document.documentElement.scrollTop;
      const clientHeight = document.documentElement.clientHeight;
      
      if (scrollHeight - scrollTop - clientHeight < 300) {
        loadMorePolls();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, loadingMore, hasMore, offset, selectedTags, searchKeyword]);

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/user/stats`);
      setStats(response.data);
      // Always set overall stats for the progress bar (never filtered)
      setOverallStats({
        totalPolls: response.data.totalPolls,
        answeredPolls: response.data.answeredPolls
      });
      
      if (response.data.tagBreakdown && response.data.tagBreakdown.length > 0) {
        setTop10Tags(response.data.tagBreakdown.slice(0, 10));
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const loadPolls = async (reset = false) => {
    try {
      setLoading(true);
      const newOffset = reset ? 0 : offset;
      const tagQuery = selectedTags.length > 0 ? `tags=${selectedTags.join(',')}` : '';
      const paginationQuery = `limit=20&offset=${newOffset}`;
      const unansweredQuery = unansweredFirst ? `unanswered_first=true` : '';
      const sortQuery = sortByInterests ? 'sort=interests' : '';
      const searchQuery = searchKeyword ? `search=${encodeURIComponent(searchKeyword)}` : '';
      
      let statusQuery = '';
      if (statusFilter === 'answered') {
        statusQuery = 'answered_only=true';
      } else if (statusFilter === 'unanswered') {
        statusQuery = 'unanswered_only=true';
      }
      
      const queryParts = [tagQuery, paginationQuery, unansweredQuery, sortQuery, statusQuery, searchQuery].filter(q => q);
      const queryString = queryParts.join('&');
      
      // Build custom polls query string
      const customQueryParts = [tagQuery, searchQuery].filter(q => q);
      const customQueryString = customQueryParts.length > 0 ? `?${customQueryParts.join('&')}` : '';
      
      // Fetch both system polls and custom polls
      const [systemResponse, customResponse] = await Promise.all([
        axios.get(`${API_URL}/api/polls?${queryString}`),
        axios.get(`${API_URL}/api/custom-polls/active${customQueryString}`).catch(() => ({ data: { polls: [] } }))
      ]);
      
      const systemData = systemResponse.data;
      let filteredCustomPolls = customResponse.data.polls || [];
      
      // Filter custom polls by answered status if needed
      if (statusFilter === 'answered') {
        filteredCustomPolls = filteredCustomPolls.filter(p => p.userAnswered);
      } else if (statusFilter === 'unanswered') {
        filteredCustomPolls = filteredCustomPolls.filter(p => !p.userAnswered);
      }
      
      if (reset) {
        setPolls(systemData.polls);
        setCustomPolls(filteredCustomPolls);
        setOffset(20);
      } else {
        setPolls(prev => [...prev, ...systemData.polls]);
        setOffset(prev => prev + 20);
      }
      
      setHasMore(systemData.hasMore);
      
      // Check favorites status for loaded polls
      const allPollIds = systemData.polls.map(p => String(p.pollId));
      if (allPollIds.length > 0) {
        try {
          const favRes = await axios.post(`${API_URL}/api/favorites/check`, { item_type: 'poll', item_ids: allPollIds });
          setFavoritedPolls(prev => reset ? favRes.data.favorited : { ...prev, ...favRes.data.favorited });
        } catch (e) { /* favorites check is non-critical */ }
      }
      
      // Update stats with filtered stats when tags are selected or search is active
      if ((selectedTags.length > 0 || searchKeyword) && systemData.filteredStats) {
        setStats(prevStats => ({
          ...prevStats,
          totalPolls: systemData.filteredStats.totalPolls + filteredCustomPolls.length,
          answeredPolls: systemData.filteredStats.answeredPolls + filteredCustomPolls.filter(p => p.userAnswered).length,
          unansweredPolls: systemData.filteredStats.unansweredPolls + filteredCustomPolls.filter(p => !p.userAnswered).length
        }));
      }
    } catch (error) {
      console.error('Failed to load polls:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMorePolls = async () => {
    if (loadingMore || !hasMore) return;
    
    try {
      setLoadingMore(true);
      const tagQuery = selectedTags.length > 0 ? `tags=${selectedTags.join(',')}` : '';
      const paginationQuery = `limit=20&offset=${offset}`;
      const unansweredQuery = unansweredFirst ? `unanswered_first=true` : '';
      const sortQuery = sortByInterests ? 'sort=interests' : '';
      const searchQuery = searchKeyword ? `search=${encodeURIComponent(searchKeyword)}` : '';
      
      let statusQuery = '';
      if (statusFilter === 'answered') {
        statusQuery = 'answered_only=true';
      } else if (statusFilter === 'unanswered') {
        statusQuery = 'unanswered_only=true';
      }
      
      const queryParts = [tagQuery, paginationQuery, unansweredQuery, sortQuery, statusQuery, searchQuery].filter(q => q);
      const queryString = queryParts.join('&');
      
      const response = await axios.get(`${API_URL}/api/polls?${queryString}`);
      const data = response.data;
      
      setPolls(prev => [...prev, ...data.polls]);
      setOffset(prev => prev + 20);
      setHasMore(data.hasMore);
    } catch (error) {
      console.error('Failed to load more polls:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  // Sanitize search input to prevent XSS and SQL injection
  const sanitizeSearchInput = (input) => {
    // Remove potentially dangerous characters and patterns
    let sanitized = input
      .replace(/[<>"'`;(){}[\]\\]/g, '') // Remove XSS-prone characters
      .replace(/(\b(SELECT|INSERT|UPDATE|DELETE|DROP|UNION|ALTER|CREATE|TRUNCATE|EXEC|EXECUTE)\b)/gi, '') // Remove SQL keywords
      .replace(/--/g, '') // Remove SQL comments
      .replace(/\/\*/g, '') // Remove SQL block comment start
      .replace(/\*\//g, '') // Remove SQL block comment end
      .trim();
    
    // Limit to 50 characters
    return sanitized.substring(0, 50);
  };

  const handleSearchInputChange = (e) => {
    const sanitized = sanitizeSearchInput(e.target.value);
    setSearchInput(sanitized);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const sanitizedKeyword = sanitizeSearchInput(searchInput);
    setSearchKeyword(sanitizedKeyword);
    setHasUserInteracted(true);
    if (!statusFilter) {
      setStatusFilter('all'); // Default to showing all polls when searching
    }
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchKeyword('');
  };

  const toggleTag = (tagId) => {
    if (selectedTags.includes(tagId)) {
      const newTags = selectedTags.filter(id => id !== tagId);
      setSelectedTags(newTags);
      // If no tags remain selected, keep current status filter
    } else {
      setSelectedTags([...selectedTags, tagId]);
      // When a tag is selected, default to showing unanswered polls
      setStatusFilter('unanswered');
    }
    setHasUserInteracted(true);
  };

  const handleStatusFilterClick = (filter) => {
    setStatusFilter(filter);
    setHasUserInteracted(true);
  };

  const clearFilters = () => {
    setSelectedTags([]);
    setSearchInput('');
    setSearchKeyword('');
    // Reset stats to original values
    loadStats();
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Overall Progress - Always based on total polls, not filtered */}
        <div className="card-container mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-gray-800 font-rubik">Overall Progress</h3>
            <span className="text-sm font-medium text-gray-600 font-rubik">
              {overallStats?.totalPolls ? Math.round((overallStats.answeredPolls / overallStats.totalPolls) * 100) : 0}% Complete
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-card h-6">
            <div
              className="bg-primary h-6 rounded-card transition-all duration-500 flex items-center justify-end pr-2"
              style={{ width: `${overallStats?.totalPolls ? Math.round((overallStats.answeredPolls / overallStats.totalPolls) * 100) : 0}%` }}
            >
              {overallStats?.totalPolls && Math.round((overallStats.answeredPolls / overallStats.totalPolls) * 100) > 10 && (
                <span className="text-white text-xs font-semibold font-rubik">
                  {Math.round((overallStats.answeredPolls / overallStats.totalPolls) * 100)}%
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Stats Overview with Filtering */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <button
            onClick={() => handleStatusFilterClick('all')}
            className={`card-container text-left transition-all transform hover:scale-105 ${
              statusFilter === 'all' 
                ? 'ring-2 ring-primary shadow-card-hover bg-gradient-to-br from-primary/5 to-primary/10' 
                : 'hover:shadow-card-hover'
            }`}
            data-testid="total-polls-card"
          >
            <div className="text-sm font-medium text-gray-500 mb-2 font-rubik">Total Polls</div>
            <div className="text-4xl font-bold text-gray-800 font-rubik">{stats?.totalPolls || 0}</div>
            {statusFilter === 'all' && (
              <div className="mt-2 text-sm text-primary font-medium font-rubik">✓ Showing all polls</div>
            )}
          </button>
          
          <button
            onClick={() => handleStatusFilterClick('answered')}
            className={`card-container text-left transition-all transform hover:scale-105 ${
              statusFilter === 'answered' 
                ? 'ring-2 ring-primary shadow-card-hover bg-gradient-to-br from-primary/5 to-primary/10' 
                : 'hover:shadow-card-hover'
            }`}
            data-testid="answered-polls-card"
          >
            <div className="text-sm font-medium text-gray-500 mb-2 font-rubik">Answered</div>
            <div className="text-4xl font-bold text-primary font-rubik">{stats?.answeredPolls || 0}</div>
            {statusFilter === 'answered' ? (
              <div className="mt-2 text-sm text-primary font-medium font-rubik">✓ Showing answered polls</div>
            ) : (
              <div className="mt-2 text-xs text-gray-400 font-rubik">Click to display results</div>
            )}
          </button>
          
          <button
            onClick={() => handleStatusFilterClick('unanswered')}
            className={`card-container text-left transition-all transform hover:scale-105 ${
              statusFilter === 'unanswered' 
                ? 'ring-2 ring-secondary shadow-card-hover bg-gradient-to-br from-secondary/5 to-secondary/10' 
                : 'hover:shadow-card-hover'
            }`}
            data-testid="remaining-polls-card"
          >
            <div className="text-sm font-medium text-gray-500 mb-2 font-rubik">Remaining</div>
            <div className="text-4xl font-bold text-secondary font-rubik">{stats?.unansweredPolls || 0}</div>
            {statusFilter === 'unanswered' && (
              <div className="mt-2 text-sm text-secondary font-medium font-rubik">✓ Showing unanswered polls</div>
            )}
          </button>
        </div>

        <TrendingTicker kind="polls" hours={24} limit={2} />

        {/* Search & Filter Section - Combined */}
        <div className="card-container mb-6" data-testid="search-filter-section">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800 font-rubik">Search & Filter</h3>
            <button
              onClick={clearFilters}
              disabled={selectedTags.length === 0 && !searchKeyword}
              className={`text-sm font-medium font-rubik transition-colors ${
                selectedTags.length > 0 || searchKeyword
                  ? 'text-danger hover:text-red-800 cursor-pointer'
                  : 'text-gray-400 cursor-not-allowed'
              }`}
              data-testid="clear-filters-button"
            >
              Clear All
            </button>
          </div>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="flex gap-3 mb-4">
            <div className="relative flex-1">
              <MdSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xl" />
              <input
                type="text"
                placeholder="Search polls by keyword..."
                value={searchInput}
                onChange={handleSearchInputChange}
                maxLength={50}
                className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-card focus:ring-2 focus:ring-secondary focus:border-secondary font-rubik text-sm"
                data-testid="search-input"
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  data-testid="clear-search-btn"
                >
                  <MdClear className="text-xl" />
                </button>
              )}
            </div>
            <button
              type="submit"
              className="px-6 py-3 bg-secondary text-white rounded-card font-rubik font-medium hover:bg-secondary/90 transition-colors"
              data-testid="search-btn"
            >
              Search
            </button>
            <button
              type="button"
              onClick={() => {
                setSortByInterests(v => !v);
                setHasUserInteracted(true);
              }}
              title="Sort polls by your favorite topics"
              className={`px-4 py-3 rounded-card font-rubik font-medium transition-colors border ${
                sortByInterests
                  ? 'bg-primary text-white border-primary hover:bg-primary/90'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-primary hover:text-primary'
              }`}
              data-testid="sort-interests-btn"
            >
              {sortByInterests ? '★ For you' : 'For you'}
            </button>
          </form>

          {/* Active Filters Display */}
          {(searchKeyword || selectedTags.length > 0) && (
            <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-card" data-testid="active-filters-display">
              <h4 className="text-xs font-semibold text-gray-500 mb-2 font-rubik uppercase tracking-wide">Active Filters</h4>
              <div className="flex flex-wrap gap-2">
                {searchKeyword && (
                  <span className="px-3 py-1.5 bg-secondary/10 text-secondary rounded-full text-sm font-medium font-rubik flex items-center gap-2">
                    <MdSearch className="text-sm" />
                    &ldquo;{searchKeyword}&rdquo;
                    <button onClick={clearSearch} className="hover:text-red-600 ml-1">×</button>
                  </span>
                )}
                {selectedTags.map(tagId => {
                  const tag = tags.find(t => t.tagId === tagId);
                  if (!tag) return null;
                  return (
                    <button
                      key={tag.tagId}
                      onClick={() => toggleTag(tag.tagId)}
                      className="px-3 py-1.5 rounded-full text-sm font-medium text-white hover:opacity-80 transition-all flex items-center gap-1"
                      style={{ backgroundColor: tag.color }}
                      data-testid={`selected-tag-${tag.name}`}
                    >
                      {tag.name} ×
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Tag Filters - Collapsible */}
          {tags.length > 0 && (
            <>
              <button
                onClick={() => setIsTagMenuCollapsed(!isTagMenuCollapsed)}
                className="w-full flex items-center justify-between py-2 text-gray-700 font-rubik font-medium hover:text-primary transition-colors"
                data-testid="toggle-tag-menu"
              >
                <span className="flex items-center gap-2">
                  {isTagMenuCollapsed ? <MdKeyboardArrowDown className="text-xl" /> : <MdKeyboardArrowUp className="text-xl" />}
                  Filter by Tags
                </span>
              </button>

              {/* Collapsible Tag Content */}
              {!isTagMenuCollapsed && (
                <div className="pt-3 border-t mt-2">
                  {/* Popular Tags */}
                  {top10Tags.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-xs font-semibold text-gray-500 mb-2 font-rubik uppercase tracking-wide">Popular Tags</h4>
                      <div className="flex flex-wrap gap-2">
                        {top10Tags.filter(tag => !selectedTags.includes(tag.tagId)).map(tag => (
                          <button
                            key={tag.tagId}
                            onClick={() => toggleTag(tag.tagId)}
                            className="px-3 py-1.5 rounded-full text-sm font-medium text-white hover:opacity-90 transition-all"
                            style={{ backgroundColor: tag.color }}
                            data-testid={`tag-quick-${tag.name}`}
                          >
                            {tag.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* View All Tags Button */}
                  <button
                    onClick={() => setShowTagDropdown(!showTagDropdown)}
                    className="w-full bg-civote-bg hover:bg-gray-300 text-gray-700 py-2 px-4 rounded-card font-medium transition-all flex items-center justify-between font-rubik text-sm"
                    data-testid="toggle-tag-dropdown"
                  >
                    <span>{showTagDropdown ? 'Hide All Tags' : `View All Tags (${tags.length})`}</span>
                    {showTagDropdown ? <MdKeyboardArrowUp className="text-xl" /> : <MdKeyboardArrowDown className="text-xl" />}
                  </button>

                  {/* All Tags Dropdown - Grouped by Category */}
                  {showTagDropdown && (
                    <GroupedTagFilter 
                      tags={tags} 
                      selectedTags={selectedTags} 
                      toggleTag={toggleTag} 
                    />
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Polls List */}
        <div className="space-y-4">
          {!hasUserInteracted ? (
            <div className="card-container text-center py-12" data-testid="select-filter-message">
              <MdInfo className="text-5xl text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2 font-rubik">Select a Filter to View Polls</h3>
              <p className="text-gray-500 font-rubik">Click on one of the cards above (Total Polls, Answered, or Remaining)</p>
              <p className="text-gray-500 font-rubik mt-1">Or select tags to filter polls by topic</p>
            </div>
          ) : loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto"></div>
              <p className="text-gray-600 mt-4">Loading polls...</p>
            </div>
          ) : polls.length === 0 && customPolls.length === 0 ? (
            <div className="card-container text-center" data-testid="no-polls-message">
              <p className="text-gray-500 text-lg">No polls available for the selected filter</p>
            </div>
          ) : (
            <>
              {/* Mixed Polls - Custom and System polls together */}
              {(() => {
                // Combine and interleave polls - custom polls first, then system polls
                const allPolls = [
                  ...customPolls.map(p => ({ ...p, isCustom: true })),
                  ...polls.map(p => ({ ...p, isCustom: false }))
                ];
                
                return allPolls.map(poll => 
                  poll.isCustom ? (
                    <CustomPollCard 
                      key={`custom-${poll.customPollId}`} 
                      poll={poll} 
                      tags={tags}
                      isOwnerView={false}
                      onPollUpdate={(pollId, isEdit) => {
                        // Only remove from list if not an edit
                        if (!isEdit) {
                          setCustomPolls(prevPolls => prevPolls.filter(p => p.customPollId !== pollId));
                          // Optimistically update counters
                          setStats(prev => ({ ...prev, answeredPolls: prev.answeredPolls + 1, unansweredPolls: Math.max(0, prev.unansweredPolls - 1) }));
                          setOverallStats(prev => ({ ...prev, answeredPolls: prev.answeredPolls + 1 }));
                        }
                      }}
                    />
                  ) : (
                    <PollCard 
                      key={`system-${poll.pollId}`} 
                      poll={poll} 
                      tags={tags}
                      showResultsByDefault={statusFilter === 'answered'}
                      initialFavorited={!!favoritedPolls[String(poll.pollId)]}
                      onPollUpdate={(pollId, isEdit) => {
                        // Only remove from list if not an edit
                        if (!isEdit) {
                          setPolls(prevPolls => prevPolls.filter(p => p.pollId !== pollId));
                          // Optimistically update counters
                          setStats(prev => ({ ...prev, answeredPolls: prev.answeredPolls + 1, unansweredPolls: Math.max(0, prev.unansweredPolls - 1) }));
                          setOverallStats(prev => ({ ...prev, answeredPolls: prev.answeredPolls + 1 }));
                        }
                      }}
                    />
                  )
                );
              })()}
              
              {/* Loading More Indicator */}
              {loadingMore && (
                <div className="text-center py-8" data-testid="loading-more">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
                  <p className="text-gray-600 mt-2">Loading more polls...</p>
                </div>
              )}
              
              {/* End of List */}
              {!hasMore && polls.length > 0 && (
                <div className="text-center py-8 text-gray-500" data-testid="end-of-list">
                  <p>You&apos;ve reached the end of the polls list</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
  );
}


function GroupedTagFilter({ tags, selectedTags, toggleTag }) {
  const [expandedGroups, setExpandedGroups] = useState({});
  const [tagSearch, setTagSearch] = useState('');

  // Build hierarchy from flat tags
  const { parentGroups, ungrouped } = useMemo(() => {
    const parentMap = {};
    const ungroupedTags = [];

    // Separate parents and children
    const parentIds = new Set();
    for (const tag of tags) {
      if (!tag.parentTagId) {
        // Check if any tag references this as parent
        const hasChildren = tags.some(t => t.parentTagId === tag.tagId);
        if (hasChildren) {
          parentMap[tag.tagId] = { ...tag, children: [] };
          parentIds.add(tag.tagId);
        }
      }
    }

    for (const tag of tags) {
      if (tag.parentTagId && parentMap[tag.parentTagId]) {
        parentMap[tag.parentTagId].children.push(tag);
      } else if (!parentIds.has(tag.tagId)) {
        ungroupedTags.push(tag);
      }
    }

    const groups = Object.values(parentMap).sort((a, b) => a.name.localeCompare(b.name));
    groups.forEach(g => g.children.sort((a, b) => a.name.localeCompare(b.name)));
    return { parentGroups: groups, ungrouped: ungroupedTags.sort((a, b) => a.name.localeCompare(b.name)) };
  }, [tags]);

  const toggleGroup = (tagId) => {
    setExpandedGroups(prev => ({ ...prev, [tagId]: !prev[tagId] }));
  };

  // Filter based on search
  const filteredGroups = tagSearch
    ? parentGroups.map(g => ({
        ...g,
        children: g.children.filter(c => c.name.toLowerCase().includes(tagSearch.toLowerCase()))
      })).filter(g => g.name.toLowerCase().includes(tagSearch.toLowerCase()) || g.children.length > 0)
    : parentGroups;

  const filteredUngrouped = tagSearch
    ? ungrouped.filter(t => t.name.toLowerCase().includes(tagSearch.toLowerCase()))
    : ungrouped;

  // If there are no parent groups, fall back to flat list
  if (parentGroups.length === 0) {
    return (
      <div className="mt-3 max-h-64 overflow-y-auto" data-testid="all-tags-scrollable">
        <div className="flex flex-wrap gap-2">
          {tags.filter(tag => !selectedTags.includes(tag.tagId)).map(tag => (
            <button
              key={tag.tagId}
              onClick={() => toggleTag(tag.tagId)}
              className="px-3 py-1 rounded-full text-sm font-medium text-white hover:opacity-90 transition-all"
              style={{ backgroundColor: tag.color }}
              data-testid={`tag-filter-${tag.name}`}
            >
              {tag.name}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3" data-testid="all-tags-scrollable">
      {/* Tag search within dropdown */}
      <div className="relative mb-3">
        <MdSearch className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Search tags..."
          value={tagSearch}
          onChange={e => setTagSearch(e.target.value)}
          className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-primary/20 focus:border-primary outline-none"
          data-testid="tag-filter-search"
        />
      </div>
      
      <div className="max-h-72 overflow-y-auto space-y-1">
        {filteredGroups.map(group => (
          <div key={group.tagId} className="border border-gray-100 rounded-lg overflow-hidden">
            {/* Category header */}
            <button
              onClick={() => toggleGroup(group.tagId)}
              className="w-full flex items-center gap-2 px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
              data-testid={`tag-group-${group.name}`}
            >
              {expandedGroups[group.tagId] ? (
                <MdKeyboardArrowUp className="text-gray-500 flex-shrink-0" />
              ) : (
                <MdKeyboardArrowDown className="text-gray-500 flex-shrink-0" />
              )}
              <span
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: group.color }}
              />
              <span className="text-sm font-semibold text-gray-700 flex-1">{group.name}</span>
              <span className="text-xs text-gray-400">{group.children.length}</span>
            </button>

            {/* Subtags */}
            {expandedGroups[group.tagId] && (
              <div className="px-3 py-2 flex flex-wrap gap-1.5">
                {group.children.filter(c => !selectedTags.includes(c.tagId)).map(child => (
                  <button
                    key={child.tagId}
                    onClick={() => toggleTag(child.tagId)}
                    className="px-2.5 py-1 rounded-full text-xs font-medium text-white hover:opacity-90 transition-all"
                    style={{ backgroundColor: child.color }}
                    data-testid={`tag-filter-${child.name}`}
                  >
                    {child.name}
                  </button>
                ))}
                {group.children.filter(c => !selectedTags.includes(c.tagId)).length === 0 && (
                  <span className="text-xs text-gray-400 italic">All selected</span>
                )}
              </div>
            )}
          </div>
        ))}

        {/* Ungrouped tags */}
        {filteredUngrouped.length > 0 && (
          <div className="border border-gray-100 rounded-lg overflow-hidden">
            <button
              onClick={() => toggleGroup('_ungrouped')}
              className="w-full flex items-center gap-2 px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
              data-testid="tag-group-ungrouped"
            >
              {expandedGroups['_ungrouped'] ? (
                <MdKeyboardArrowUp className="text-gray-500 flex-shrink-0" />
              ) : (
                <MdKeyboardArrowDown className="text-gray-500 flex-shrink-0" />
              )}
              <span className="text-sm font-semibold text-gray-700 flex-1">Other</span>
              <span className="text-xs text-gray-400">{filteredUngrouped.length}</span>
            </button>
            {expandedGroups['_ungrouped'] && (
              <div className="px-3 py-2 flex flex-wrap gap-1.5">
                {filteredUngrouped.filter(t => !selectedTags.includes(t.tagId)).map(tag => (
                  <button
                    key={tag.tagId}
                    onClick={() => toggleTag(tag.tagId)}
                    className="px-2.5 py-1 rounded-full text-xs font-medium text-white hover:opacity-90 transition-all"
                    style={{ backgroundColor: tag.color }}
                    data-testid={`tag-filter-${tag.name}`}
                  >
                    {tag.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


export default PollsPage;
